import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { LocationService } from '@usheru-hff/sdk/location';
import { Movie } from '@usheru-hff/shared/domain';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { MovieService } from './movie.service';

const MOVIE_SITE_PAGES: string[] = ['film-info', 'book-now', 'watch-now'];

export const moviesTypeResolver = (route: ActivatedRouteSnapshot) => {
  const type = route.data?.['type'];
  return { type };
};

export const isPartialMovieLoadedResolver: ResolveFn<boolean> = (): Observable<boolean> => {
  const router = inject(Router);

  const navigation = router.getCurrentNavigation();
  const navState = !!navigation?.extras?.state?.['movie'];
  // console.log('navState', navState);
  return of(navState);
};
/**
 * A Resolver that returns a Movie (or null if not found).
 * It implements the same logic as the guard:
 * 1. Uses SSR to fetch/store in TransferState or reuses SSR data on the browser
 * 2. Checks whether the route is valid for branded vs. non-branded
 * 3. If invalid, navigates away and returns null
 */
export const MoviePageResolver: ResolveFn<Movie | null> = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): Observable<Movie | null> => {
  // Services or tokens we need
  const router = inject(Router);
  const movieService = inject(MovieService);
  const locationService = inject(LocationService);
  const messageService = inject(MessageService);
  const translationsService = inject(TranslationsService);
  const showtimeStreamingService = inject(ShowtimeStreamingService);
  const navigation = router.getCurrentNavigation();
  const navState = navigation?.extras?.state;
  const partialMovie: Movie = navState ? navState['movie'] : null;

  // Extract the slug
  const slug: string = route.params['slug'];

  if (partialMovie) {
    return checkBrandedPaths(route, partialMovie);
  }

  if (!slug) {
    router.navigateByUrl('/not-found');
    return of(null);
  }

  // console.log('state', _state);

  return fetchMovie(slug).pipe(switchMap(movie => checkBrandedPaths(route, movie)));

  function fetchMovie(slug: string): Observable<Movie | null> {
    const langCode = translationsService.currentLanguage().code;
    const countrySlug = locationService.selectedCountry?.slug;
    return movieService.getMovie(slug, langCode, countrySlug).pipe(
      catchError(() => {
        messageService.add({
          severity: 'warn',
          summary: translationsService.translate('movie.page', 'Movie page'),
          detail: translationsService
            .translate('movie.not-found', 'The movie _movie_ is not present. Try a different title please.')
            .replace('_movie_', slug)
        });
        router.navigateByUrl('/home');

        return of(null);
      })
    );
  }

  /**
   * Evaluate brand logic. If the path is not valid for the type
   * of movie (branded vs. non-branded), we do a redirect and return null.
   * Otherwise, return an observable of the movie.
   */
  function checkBrandedPaths(route: ActivatedRouteSnapshot, movie: Movie | null): Observable<Movie | null> {
    if (!movie) {
      router.navigateByUrl('/not-found');
      return of(null);
    }

    const isBranded = !!movie.settings?.movieSiteEnabled;
    const pathSegment = route.routeConfig?.path || ''; // e.g. ':slug' or ':slug/film-info'

    // If it's a branded movie and the path is just ':slug', redirect to film-info
    if (isBranded && pathSegment === ':slug') {
      const slug = movie.friendlyUrl;
      const routeName = routeByPriority(movie);
      router.navigate([`/movie/${slug}/${routeName}`]);
      return of(null);
    }

    // If user tries a brand-only path but the movie is not branded
    // (like film-info, book-now, watch-now, etc.)
    if (!isBranded && MOVIE_SITE_PAGES.includes(pathSegment)) {
      router.navigate([`/movie/${movie.friendlyUrl}`]);
      return of(null);
    }

    // If valid, return the movie
    return of(movie);
  }
  function routeByPriority(movie: Movie): 'film-info' | 'book-now' | 'watch-now' {
    if (movie?.settings?.movieSiteAutomatedSwitch) {
      return showtimeStreamingService.determineInCinemasAvailability(movie) ? 'book-now' : 'film-info';
    }

    switch (movie.settings?.movieSiteMode || '') {
      case 'AWARENESS':
        return 'film-info';
      case 'CONVERSION':
        return 'book-now';
      default:
        return showtimeStreamingService.determineInCinemasAvailability(movie) ? 'book-now' : 'film-info';
    }
  }
};
